/**
 * Fábrica de Anúncios — regras da tela, sem React nem MUI.
 *
 * A tela guia a criação de um anúncio NOVO no Mercado Livre pelo método "Fábrica de Anúncio"
 * (UP Digital; caso-modelo Árvore de Natal 834 galhos, set/2026), com o serviço
 * `services/fabrica-anuncios` fazendo pesquisa (API do ML), proposta (Claude), preço, fotos
 * (OpenAI gpt-image ou Gemini) e, só no fim e só com clique explícito, o POST /items PAUSADO.
 *
 * Regras de ouro (skill `fabrica-de-anuncio`):
 *  - nada é gravado no ML sem "Publicar pausado" + confirmação; o anúncio nasce pausado;
 *  - só se declara o que é comprovado — `[A CONFIRMAR …]` é destacado e bloqueia a publicação;
 *  - título de anúncio 56–60 caracteres, sem marca de terceiro; catálogo 185 exatos;
 *  - margem de contribuição mínima 10%; acima de R$ 79 o frete grátis é obrigatório.
 */

// ---------------------------------------------------------------------------------------------
// Tipos (espelho do JSON do serviço)
// ---------------------------------------------------------------------------------------------

export type ContaMl = 'FIESTAPARTYFESTA' | 'FIESTAPARTYHOME'
export const CONTAS: ContaMl[] = ['FIESTAPARTYFESTA', 'FIESTAPARTYHOME']

export const BLOCOS = ['titulo', 'preco', 'imagens', 'ficha_primaria', 'ficha_secundaria', 'descricao'] as const
export type Bloco = (typeof BLOCOS)[number]
export const BLOCO_LABEL: Record<Bloco, string> = {
  titulo: 'Título',
  preco: 'Preço',
  imagens: 'Imagens',
  ficha_primaria: 'Características principais',
  ficha_secundaria: 'Outras características',
  descricao: 'Descrição',
}

export type StatusBloco = 'pendente' | 'aprovado' | 'editado'
export type Aprovacao = { status: StatusBloco; valor?: unknown; nota?: string }

export type Embalagem = { altura?: number; largura?: number; comprimento?: number; peso_g?: number }

export type Canal = 'ml' | 'shopee'
export type LojaShopee = '1' | '2'
export const LOJAS_SHOPEE: Record<LojaShopee, string> = { '1': 'FIESTA PARTY FESTA (Loja 1)', '2': 'FIESTA PARTY DECORA (Loja 2)' }

export type Produto = {
  nome: string
  marca: string
  conta: ContaMl
  canais?: Canal[]
  loja_shopee?: LojaShopee
  termo_busca: string
  sku?: string
  ean?: string
  embalagem?: Embalagem
  estoque?: number | null
  cmv?: number | null
  impostos_pct?: number | null
  confirmado?: string
  /** Pesquisa v2 (25/09/2026): links de campeões (ML e Shopee), 1 por linha, e o modo — "auto" = ranking
   *  automático + links (links vêm primeiro); "links" = só os links. Complementares: o operador escolhe. */
  concorrentes?: string[]
  modo_pesquisa?: ModoPesquisa
  preco_referencia?: number | null
}

export type ModoPesquisa = 'auto' | 'links'
export const MAX_CONCORRENTES = 10

export type ConcorrenteParse = { entrada: string; canal: 'ml' | 'shopee' | null; mlb: string | null; catalogo: string | null; shop_id: string | null; item_id: string | null; erro: string | null; proprio?: boolean }

export type Campeao = {
  canal: 'ml' | 'shopee'
  origem?: 'link' | 'ranking'
  mlb?: string | null
  item_id?: string
  entrada?: string
  erros: string[]
  item?: { titulo?: string; preco?: number | null; preco_original?: number | null; tipo?: string; logistica?: string; frete_gratis?: boolean; catalogo?: string | null; n_fotos?: number; n_variacoes?: number; fotos?: string[]; permalink?: string; garantia?: string | null; atributos?: Array<{ id: string; nome?: string; valor: string }> }
  descricao?: string
  vendedor?: { nickname?: string; nome?: string; reputacao?: string; avaliacao?: number; loja_oficial?: boolean }
  avaliacoes?: { media?: number | null; total?: number | null; textos?: Array<{ nota: number; texto: string }> }
  perguntas?: Array<{ texto: string; resposta: string | null }>
  pagina?: { status: string; vendidos?: number | null; mais_vendido?: boolean; parcelamento?: string | null; erro?: string }
  vendidos?: number | null
  vendidos_fonte?: 'pagina' | 'api' | 'ranking' | 'nao_medido'
  buy_box?: { catalogo: string; vencedor?: string | null; preco?: number | null; concorrentes_no_catalogo?: number | null; preco_min_catalogo?: number; preco_max_catalogo?: number }
}

export type ComProva = { mlbs?: string[] }
export type AnaliseCampeoes = {
  erro?: string
  sem_prova?: string[]
  resumo?: string
  titulos?: { estrutura_dominante?: string; termos_comuns?: Array<ComProva & { termo: string; em: number }>; termos_exclusivos?: Array<ComProva & { termo: string; em: number }>; marcas_terceiros?: string[]; brecha?: string[] }
  ficha?: { atributos?: Array<{ id: string; preenchem: number; valores?: Record<string, number> }>; permitidos_que_ninguem_preenche?: string[]; obrigatorios_faltando_no_produto?: string[] }
  descricao?: { secoes_comuns?: Array<{ secao: string; em: number }>; tamanho_medio_caracteres?: number; tom?: string; o_que_falta_em_todas?: string[] }
  avaliacoes?: { media_ponderada?: number; total?: number; decepciona?: Array<ComProva & { tema: string; freq: number; exemplo?: string }>; encanta?: Array<ComProva & { tema: string; freq: number; exemplo?: string }> }
  perguntas?: { faq_obrigatoria?: Array<ComProva & { pergunta: string; em_quantos_anuncios: number; resposta_padrao_dos_campeoes?: string }>; sem_resposta?: Array<{ pergunta: string; mlb: string }> }
  visual?: { capa?: { fundo_branco?: number; com_texto?: number; produto_inteiro?: number }; fotos_padrao?: string[]; brecha?: string[]; observacao?: string }
  catalogo?: { campeoes_em_catalogo?: number; observacao?: string }
  diferenciais_proprios?: string[]
  objecoes_para_o_anuncio?: Array<{ objecao: string; responder_em: string[] }>
  com_grade?: boolean
  modelo?: string
}

export type Posicionamento = {
  n: number
  min?: number
  max?: number
  mediana?: number
  media?: number
  media_ponderada_por_vendidos?: number
  vendidos_medidos_em?: number
  preco_proprio?: number
  posicao_do_proprio?: string
  por_campeao: Array<{ canal: string; id: string; titulo: string; preco: number; preco_original?: number | null; tipo?: string; full?: boolean; frete_gratis?: boolean; parcelamento?: string | null; vendidos?: number | null; vendidos_fonte?: string; visitas_30d?: number | null; visitas_total?: number | null; reputacao?: string | number | null; loja_oficial?: boolean; catalogo?: string | null; media_avaliacoes?: number | null; permalink?: string }>
}

export type Comparacao = {
  tem_proprios: boolean
  n_campeoes_ml?: number
  fotos_por_campeao_media?: number
  proprios?: Array<{ id: string; titulo: string; preco?: number | null; status?: string; atributos_que_campeoes_preenchem_e_eu_nao: string[]; preco_vs_mediana_pct?: number }>
}

export type ItemRanking = {
  mlb: string | null
  titulo: string
  preco: number | null
  vendidos: number
  vendedor: string | null
  catalogo: string | null
  permalink: string | null
}

export type Pesquisa = {
  status: 'nenhuma' | 'rodando' | 'ok' | 'erro' | 'aguardando_links'
  erro?: string
  erros?: string[]
  avisos?: string[]
  modo?: ModoPesquisa
  /** v2: ranking falhou (ou modo "só links" sem link) — a tela pede os links. */
  motivo_links?: string
  concluido_em?: string
  concorrentes_parse?: ConcorrenteParse[]
  campeoes?: Campeao[]
  analise?: AnaliseCampeoes
  posicionamento?: Posicionamento
  comparacao?: Comparacao
  catalogo_campeoes?: { em_catalogo: number; buy_box: NonNullable<Campeao['buy_box']>[] }
  grade?: { ok: boolean; motivo?: string; campeoes?: number; fotos?: number }
  conta_propria?: Array<{ id: string; titulo: string; status: string; vendidos: number; catalogo: string | null; marca: string | null; sku: string | null; permalink: string | null }>
  inconsistencias?: string[]
  catalogo_proprio?: { id: string; catalogo: string } | null
  categoria?: { id: string; nome: string; caminho: string; max_title_length: number; obrigatorios: Array<{ id: string; nome: string }>; condicionais: Array<{ id: string; nome: string }> }
  ranking?: { indisponivel: boolean; motivo?: string; fonte?: string | null; itens: ItemRanking[]; pulado?: boolean }
  // por_estrela e sem_textos_motivo existem por causa da Shopee: ela dá a nota e a distribuição,
  // mas não libera o texto das avaliações (o ML libera) — a tela precisa dizer isso, não ficar vazia.
  avaliacoes?: Array<{ mlb: string; media: number | null; total: number | null; textos: Array<{ nota: number; titulo: string; texto: string }>; por_estrela?: Record<string, number>; sem_textos_motivo?: string | null }>
  palavras?: Array<{ palavra: string; peso: number }>
  nao_usar?: string[]
  cmv?: { ok: boolean; encontrado?: boolean; cmv?: number; impostos_pct?: number; sku?: string; erro?: string }
  shopee?: PesquisaShopee
}

export type PesquisaShopee = {
  loja: string
  loja_nome: string
  shop_id: string
  erros: string[]
  shop?: { shop_name: string; status: string }
  canais?: Array<{ id: number; nome: string }>
  categorias?: number[]
  categoria_id?: number
  atributos?: Array<{ id: number; nome: string; obrigatorio: boolean }>
  marcas?: Array<{ id: number; nome: string }>
}

export type FichaItem = { id: string; nome: string; valor: string; fonte: string; campeoes_preenchem?: number }

export type Proposta = {
  status: 'nenhuma' | 'rodando' | 'ok' | 'erro'
  erro?: string
  titulos?: Array<{ texto: string; tamanho: number }>
  titulos_catalogo?: Array<{ texto: string; tamanho: number }>
  descricao?: string
  ficha?: FichaItem[]
  faq?: Array<{ pergunta: string; resposta: string }>
  avisos?: string[]
  aprendizados?: { objecao_1?: string; decepciona?: string[]; encanta?: string[]; cada_objecao_vira?: string[] }
  a_confirmar?: string[]
  modelo?: string
}

export type LinhaPreco = {
  preco: number
  comissao_pct?: number | null
  comissao?: number | null
  fixa?: number
  frete: number
  frete_gratis_obrigatorio: boolean
  impostos: number
  mc: number
  mc_pct: number | null
  abaixo_do_minimo: boolean
  taxa_fixa_50?: boolean
  parcela_12x?: number
  erro_comissao?: string
  erro_frete?: string
}
export type Precos = { listing?: string; cmv?: number; impostos_pct?: number; dims?: string | null; tabela?: LinhaPreco[]; avisos?: string[] }

/** Regra da Mariana (24/09/2026): MC alvo 25% e preço cheio 40% acima do alvo para dar desconto. */
export type PrecosShopee = {
  ok?: boolean
  erro?: string
  loja?: string
  cmv?: number
  taxa_pct?: number
  taxa_fonte?: string
  taxa_pedidos?: number
  impostos_pct?: number
  impostos_fonte?: string
  mc_alvo_pct?: number
  markup?: number
  preco_alvo?: number
  preco_cheio?: number
  desconto_pct?: number
  mc_no_alvo?: number
  mc_no_alvo_pct?: number
  mc_no_cheio?: number
  mc_no_cheio_pct?: number
  aviso?: string | null
}

export type PublicacaoShopee = { status: string; item_id?: number; link?: string; erro?: string; pendencias?: string[]; desconto?: { discount_id?: number; ok?: boolean; erro?: string } }

export type FotoPlano = { n: number; tipo: string; tema: string; responde?: string | null; objecao?: string | null }
export type FotoGerada = {
  status: 'gerando' | 'ok' | 'erro'
  erro?: string
  prompt?: string
  largura?: number | null
  altura?: number | null
  ampliada?: boolean
  aprovacao?: 'pendente' | 'aprovada' | 'ajuste'
  ajuste?: string
  provedor?: string
  modelo?: string
  /** Auditoria automática por visão (Claude): foto gerada × foto real. */
  auditoria?: { feita: boolean; aprovada?: boolean; motivo?: string; tentativas?: Array<{ n: number; aprovada: boolean; motivo: string }> }
}

export type Publicacao = { status: string; item_id?: string; permalink?: string | null; erro?: string; pendencias?: string[]; sub_status?: string[]; fotos_processadas?: boolean; aviso_fotos?: string | null; catalogo?: { ok?: boolean; motivo?: string; sugestao_id?: string; http?: number; tentado?: boolean } | null }

export type Projeto = {
  id: string
  criado_em: string
  atualizado_em: string
  produto: Produto
  fotos_reais: Array<{ arquivo: string; nome: string }>
  pesquisa: Pesquisa
  proposta: Proposta
  precos: Precos
  precos_shopee?: PrecosShopee
  publicacao_shopee?: PublicacaoShopee
  fotos: { status: string; erro?: string; plano: FotoPlano[]; itens: Record<string, FotoGerada> }
  aprovacao: Record<Bloco, Aprovacao> & { preco_shopee?: Aprovacao }
  publicacao: Publicacao
}

export type Referencia = {
  id: string
  marca: string
  marca_slug: string
  tipo: 'foto' | 'texto' | 'anuncio'
  nome: string
  nota: string
  texto: string
  arquivo: string | null
  fotos_url: string[]
  origem: string
  criado_em: string
}

export type StatusServico = {
  ok: boolean
  texto: { chave: boolean; modelo: string }
  imagem: { provedor: string; pronto: boolean; openai: { chave: boolean; modelo: string }; gemini: { chave: boolean; modelo: string } }
  ml: { mongo: boolean }
  shopee?: { mongo: boolean; lojas: Record<string, string>; mc_alvo_pct: number; markup: number; taxa_padrao_pct: number }
  escrita_liberada: boolean
  scraping?: { ligado: boolean; pronto: boolean; ultimo?: { quando?: string; ok?: boolean; fonte?: string; motivo?: string } }
  pesquisa_v2?: { max_links: number; campeoes_do_ranking: number; pagina_ml: boolean; shopee_cookies: boolean }
}

export type Checagem = { nivel: 'erro' | 'aviso'; texto: string }

// ---------------------------------------------------------------------------------------------
// Etapas
// ---------------------------------------------------------------------------------------------

export const ETAPAS = ['Produto', 'Pesquisa', 'Proposta', 'Preço', 'Fotos', 'Aprovação', 'Publicar'] as const

/** Primeira etapa ainda não concluída — onde a tela abre ao retomar um projeto. */
export function etapaSugerida(p: Projeto | null): number {
  if (!p) return 0
  if (p.publicacao?.status === 'pausado' || p.publicacao_shopee?.status === 'unlist') return 6
  if (p.pesquisa?.status !== 'ok') return p.fotos_reais.length ? 1 : 0
  if (p.proposta?.status !== 'ok') return 2
  if (!p.precos?.tabela?.length) return 3
  if (!Object.values(p.fotos?.itens || {}).some((f) => f.status === 'ok')) return 4
  if (blocosAprovados(p) < BLOCOS.length) return 5
  return 6
}

// ---------------------------------------------------------------------------------------------
// Títulos
// ---------------------------------------------------------------------------------------------

export const TITULO_MIN = 56
export const TITULO_MAX = 60
export const TITULO_CATALOGO = 185

/** Conta caracteres como o ML conta (unidades UTF-16 são o que ele limita na prática). */
export function tamanhoTitulo(t: string): number {
  return (t || '').trim().length
}

export function checarTitulo(t: string, naoUsar: string[] = []): Checagem[] {
  const out: Checagem[] = []
  const n = tamanhoTitulo(t)
  if (n === 0) return [{ nivel: 'erro', texto: 'Título vazio.' }]
  if (n > TITULO_MAX) out.push({ nivel: 'erro', texto: `${n} caracteres — o ML corta em ${TITULO_MAX}.` })
  else if (n < TITULO_MIN) out.push({ nivel: 'aviso', texto: `${n} caracteres — o padrão UP usa ${TITULO_MIN} a ${TITULO_MAX}.` })
  const palavras = t.toLowerCase().split(/\s+/).filter((w) => w.length > 2)
  const repetidas = palavras.filter((w, i) => palavras.indexOf(w) !== i)
  if (repetidas.length) out.push({ nivel: 'aviso', texto: `Palavra repetida: ${[...new Set(repetidas)].join(', ')}.` })
  const baixo = t.toLowerCase()
  for (const m of naoUsar) {
    const mm = (m || '').toLowerCase().trim()
    if (mm && mm.length > 2 && baixo.includes(mm)) out.push({ nivel: 'erro', texto: `Contém marca de terceiro: ${m}.` })
  }
  if (/[\u{1F300}-\u{1FAFF}]/u.test(t)) out.push({ nivel: 'erro', texto: 'Sem emoji no título.' })
  return out
}

export function checarTituloCatalogo(t: string): Checagem[] {
  const n = tamanhoTitulo(t)
  if (n === TITULO_CATALOGO) return []
  return [{ nivel: 'aviso', texto: `${n} caracteres — o título de catálogo pede exatamente ${TITULO_CATALOGO}.` }]
}

// ---------------------------------------------------------------------------------------------
// [A CONFIRMAR] — destaque e bloqueio
// ---------------------------------------------------------------------------------------------

const RE_CONFIRMAR = /\[A CONFIRMAR[^\]]*\]/gi

export function temAConfirmar(texto: string | null | undefined): boolean {
  RE_CONFIRMAR.lastIndex = 0
  return RE_CONFIRMAR.test(texto || '')
}

/** Quebra o texto em trechos { texto, marcado } para a tela pintar de amarelo o que falta confirmar. */
export function segmentarAConfirmar(texto: string): Array<{ texto: string; marcado: boolean }> {
  const out: Array<{ texto: string; marcado: boolean }> = []
  let ultimo = 0
  const re = new RegExp(RE_CONFIRMAR.source, 'gi')
  let m: RegExpExecArray | null
  while ((m = re.exec(texto || '')) !== null) {
    if (m.index > ultimo) out.push({ texto: texto.slice(ultimo, m.index), marcado: false })
    out.push({ texto: m[0], marcado: true })
    ultimo = m.index + m[0].length
  }
  if (ultimo < (texto || '').length) out.push({ texto: texto.slice(ultimo), marcado: false })
  return out
}

// ---------------------------------------------------------------------------------------------
// Preço / margem de contribuição (o serviço calcula com a API; isto é o mesmo cálculo,
// puro, para a tela simular antes de chamar e para o teste conferir a fórmula)
// ---------------------------------------------------------------------------------------------

export const MC_MINIMA_PCT = 10
export const FRETE_GRATIS_A_PARTIR = 79

export function calcularMc(args: { preco: number; comissaoPct: number; fixa?: number; frete: number; impostosPct: number; cmv: number }) {
  const comissao = (args.preco * args.comissaoPct) / 100 + (args.fixa || 0)
  const impostos = (args.preco * args.impostosPct) / 100
  const mc = args.preco - comissao - args.frete - impostos - args.cmv
  const mcPct = args.preco > 0 ? (100 * mc) / args.preco : 0
  return { comissao, impostos, mc, mcPct, abaixoDoMinimo: mcPct < MC_MINIMA_PCT }
}

/** Sugestão de 4 preços em volta de um preço-base (arredondados em ,90). */
export function sugerirPrecos(base: number): number[] {
  if (!base || base <= 0) return []
  const passos = [-0.05, 0, 0.05, 0.1]
  return passos.map((p) => Math.max(1, Math.floor(base * (1 + p)) + 0.9)).map((v) => Math.round(v * 100) / 100)
}

// ---------------------------------------------------------------------------------------------
// Shopee — a mesma conta do serviço (core/shopee.py), pura, para a tela simular e o teste conferir
// ---------------------------------------------------------------------------------------------

export const SHOPEE_MC_ALVO_PCT = 25
export const SHOPEE_MARKUP_CHEIO = 1.4

/** Preço-alvo que dá a MC alvo (P = cmv ÷ (1 − taxa − impostos − mc)) e o preço cheio (alvo × markup). */
export function precificarShopee(args: { cmv: number; taxaPct: number; impostosPct: number; mcAlvoPct?: number; markup?: number }) {
  const mc = args.mcAlvoPct ?? SHOPEE_MC_ALVO_PCT
  const markup = args.markup ?? SHOPEE_MARKUP_CHEIO
  const den = 1 - args.taxaPct / 100 - args.impostosPct / 100 - mc / 100
  if (args.cmv <= 0) return { ok: false as const, erro: 'CMV zero/ausente — não dá para precificar.' }
  if (den <= 0) return { ok: false as const, erro: 'Taxa + impostos + MC alvo passam de 100%.' }
  const alvo = Math.round((args.cmv / den) * 100) / 100
  const cheio = Math.round(alvo * markup * 100) / 100
  const mcAlvo = alvo - (alvo * args.taxaPct) / 100 - (alvo * args.impostosPct) / 100 - args.cmv
  return { ok: true as const, precoAlvo: alvo, precoCheio: cheio, descontoPct: Math.round((1 - alvo / cheio) * 1000) / 10, mcAlvoPct: (100 * mcAlvo) / alvo }
}

export function temCanalShopee(p: Projeto | null): boolean {
  return !!p?.produto?.canais?.includes('shopee')
}

export function precoShopeeAprovado(p: Projeto): boolean {
  const a = p.aprovacao?.preco_shopee
  return !!p.precos_shopee?.ok && !!a && ['aprovado', 'editado'].includes(a.status)
}

/** Checklist da Shopee — espelho de `_shopee_pronto` no serviço. */
export function pendenciasShopee(p: Projeto): string[] {
  const out: string[] = []
  if (!temCanalShopee(p)) return ['Canal Shopee não marcado em Produto.']
  if (!p.pesquisa?.shopee?.categoria_id) out.push('Pesquisa Shopee sem categoria (pesquise de novo com o canal marcado).')
  for (const b of ['titulo', 'imagens', 'ficha_primaria', 'descricao'] as Bloco[]) if (!['aprovado', 'editado'].includes(p.aprovacao?.[b]?.status || '')) out.push(`Bloco "${BLOCO_LABEL[b]}" não aprovado.`)
  if (!precoShopeeAprovado(p)) out.push('Preço da Shopee não calculado/aprovado.')
  const d = descricaoFinal(p)
  if (!d.trim() || temAConfirmar(d)) out.push('Descrição vazia ou com [A CONFIRMAR].')
  if (fotosAprovadas(p).length === 0) out.push('Nenhuma foto aprovada.')
  const e = p.produto?.embalagem || {}
  if (!(e.altura && e.largura && e.comprimento && e.peso_g)) out.push('Embalagem (peso e medidas) obrigatória na Shopee.')
  if (p.produto?.estoque === null || p.produto?.estoque === undefined) out.push('Estoque não informado.')
  return out
}

export function podePublicarShopee(p: Projeto): boolean {
  return pendenciasShopee(p).length === 0
}

// ---------------------------------------------------------------------------------------------
// Aprovação e publicação
// ---------------------------------------------------------------------------------------------

export function blocosAprovados(p: Projeto): number {
  return BLOCOS.filter((b) => ['aprovado', 'editado'].includes(p.aprovacao?.[b]?.status || '')).length
}

export function fotosAprovadas(p: Projeto): number[] {
  return Object.entries(p.fotos?.itens || {})
    .filter(([, f]) => f.status === 'ok' && f.aprovacao === 'aprovada')
    .map(([n]) => Number(n))
    .sort((a, b) => a - b)
}

/** Título que vai para o ML: o editado, senão a 1ª opção da proposta. */
export function tituloFinal(p: Projeto): string {
  const a = p.aprovacao?.titulo
  if (a?.status === 'editado' && typeof a.valor === 'string' && a.valor.trim()) return a.valor.trim()
  return p.proposta?.titulos?.[0]?.texto || ''
}

export function descricaoFinal(p: Projeto): string {
  const a = p.aprovacao?.descricao
  if (a?.status === 'editado' && typeof a.valor === 'string' && a.valor.trim()) return a.valor
  return p.proposta?.descricao || ''
}

export function precoFinal(p: Projeto): number | null {
  const a = p.aprovacao?.preco
  const v = Number(a?.valor)
  return a && ['aprovado', 'editado'].includes(a.status) && Number.isFinite(v) && v > 0 ? v : null
}

/** Checklist final (passo 8 da skill) — o que ainda impede o "Publicar pausado". */
export function pendenciasPublicacao(p: Projeto): string[] {
  const out: string[] = []
  for (const b of BLOCOS) if (!['aprovado', 'editado'].includes(p.aprovacao?.[b]?.status || '')) out.push(`Bloco "${BLOCO_LABEL[b]}" não aprovado.`)
  const t = tituloFinal(p)
  if (!t) out.push('Sem título.')
  else if (tamanhoTitulo(t) > TITULO_MAX) out.push(`Título com ${tamanhoTitulo(t)} caracteres (máx. ${TITULO_MAX}).`)
  if (checarTitulo(t, p.pesquisa?.nao_usar || []).some((c) => c.nivel === 'erro' && c.texto.includes('terceiro'))) out.push('Título com marca de terceiro.')
  const d = descricaoFinal(p)
  if (!d.trim()) out.push('Descrição vazia.')
  else if (temAConfirmar(d)) out.push('Descrição ainda tem [A CONFIRMAR].')
  if (precoFinal(p) === null) out.push('Preço não aprovado.')
  const linha = p.precos?.tabela?.find((l) => l.preco === precoFinal(p))
  if (linha && linha.abaixo_do_minimo) out.push(`MC do preço aprovado abaixo de ${MC_MINIMA_PCT}%.`)
  if (!p.pesquisa?.categoria?.id) out.push('Categoria não pesquisada.')
  if (fotosAprovadas(p).length === 0) out.push('Nenhuma foto aprovada.')
  if (!fotosAprovadas(p).includes(1)) out.push('A capa (foto 1) não está aprovada.')
  const e = p.produto?.embalagem || {}
  if (!(e.altura && e.largura && e.comprimento && e.peso_g)) out.push('Embalagem (altura, largura, comprimento, peso) incompleta.')
  if (p.produto?.estoque === null || p.produto?.estoque === undefined || p.produto?.estoque === ('' as unknown)) out.push('Estoque não informado.')
  if (!p.produto?.ean) out.push('Sem EAN: vai com EMPTY_GTIN_REASON (confirme que o produto não tem código).')
  for (const f of p.proposta?.ficha || []) if (temAConfirmar(f.valor)) out.push(`Ficha: ${f.nome} está [A CONFIRMAR].`)
  return out
}

export function podePublicar(p: Projeto): boolean {
  return pendenciasPublicacao(p).filter((x) => !x.startsWith('Sem EAN')).length === 0
}

// ---------------------------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------------------------

export function produtoVazio(): Produto {
  return { nome: '', marca: 'FIESTA PARTY FESTA', conta: 'FIESTAPARTYFESTA', canais: ['ml'], loja_shopee: '1', termo_busca: '', sku: '', ean: '', embalagem: {}, estoque: null, cmv: null, impostos_pct: null, confirmado: '' }
}

export function checarProduto(pr: Produto): Checagem[] {
  const out: Checagem[] = []
  if (!pr.nome.trim()) out.push({ nivel: 'erro', texto: 'Nome do produto é obrigatório.' })
  if (!pr.marca.trim()) out.push({ nivel: 'erro', texto: 'Marca é obrigatória (é o BRAND do anúncio).' })
  if (!pr.termo_busca.trim()) out.push({ nivel: 'aviso', texto: 'Sem termo de busca, a pesquisa usa o nome do produto.' })
  return out
}

export function normalizarMlb(s: string): string | null {
  const m = (s || '').toUpperCase().match(/MLB-?(\d{6,})/)
  return m ? `MLB${m[1]}` : null
}

export function fmtData(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' })
}

// ---------------------------------------------------------------------------------------------
// Pesquisa v2 — links de campeões (parse local só para feedback imediato; o serviço é quem vale)
// ---------------------------------------------------------------------------------------------

const RE_ML_CAT = /\/p\/(MLB\d{5,})/i
const RE_ML_ITEM = /MLB-?(\d{7,})/gi
const RE_SHOPEE = /shopee\.[a-z.]+\/product\/(\d+)\/(\d+)|[?&/-]i\.(\d+)\.(\d+)/i
const RE_CURTO = /^(https?:\/\/)?((www\.)?mercadolivre\.com(\.br)?\/sec\/|s\.shopee\.com\.br\/|shp\.ee\/)/i

/** Uma linha colada → canal e ids reconhecidos, ou erro. Mesma regra do `core/concorrentes.py`. */
export function parseConcorrente(linha: string): ConcorrenteParse {
  const t = (linha || '').trim()
  const out: ConcorrenteParse = { entrada: t, canal: null, mlb: null, catalogo: null, shop_id: null, item_id: null, erro: null }
  if (!t) return { ...out, erro: 'linha vazia' }
  if (RE_CURTO.test(t)) return out // encurtado: o serviço segue o redirect e reconhece
  const cat = RE_ML_CAT.exec(t)
  RE_ML_ITEM.lastIndex = 0
  let item: RegExpExecArray | null = null
  let m: RegExpExecArray | null
  while ((m = RE_ML_ITEM.exec(t)) !== null) {
    if (!cat || `MLB${m[1]}` !== cat[1]) {
      item = m
      break
    }
  }
  const sp = /shopee\./i.test(t) ? RE_SHOPEE.exec(t) : null
  if (sp) return { ...out, canal: 'shopee', shop_id: sp[1] || sp[3], item_id: sp[2] || sp[4] }
  if (cat && !item) return { ...out, canal: 'ml', catalogo: cat[1] }
  if (item) return { ...out, canal: 'ml', mlb: `MLB${item[1]}`, catalogo: cat ? cat[1] : null }
  if (/shopee\./i.test(t)) return { ...out, erro: 'link da Shopee sem shop_id/item_id reconhecível (use o link completo do produto)' }
  return { ...out, erro: 'não reconheci como anúncio do Mercado Livre (MLB…) nem da Shopee' }
}

/** Texto do campo (1 por linha) → linhas parseadas, sem duplicados, até MAX_CONCORRENTES válidas. */
export function parseConcorrentes(texto: string): ConcorrenteParse[] {
  const vistos = new Set<string>()
  const out: ConcorrenteParse[] = []
  for (const ln of (texto || '').split(/[\r\n]+/)) {
    if (!ln.trim()) continue
    const c = parseConcorrente(ln)
    const chave = c.mlb || c.catalogo || (c.item_id ? `${c.shop_id}/${c.item_id}` : null)
    if (chave) {
      if (vistos.has(chave)) continue
      vistos.add(chave)
    }
    out.push(c)
    if (out.filter((x) => !x.erro).length >= MAX_CONCORRENTES) break
  }
  return out
}

/** A pesquisa pode disparar a proposta? (status ok; "aguardando_links" ainda não). */
export function pesquisaConcluida(p: Projeto | null): boolean {
  return p?.pesquisa?.status === 'ok'
}

/** "N de M campeões medidos" — vendidos vindos da página/ranking; "não medido" nunca vira zero. */
export function vendidosMedidos(campeoes: Campeao[] | undefined): { medidos: number; total: number } {
  const lista = campeoes || []
  return { medidos: lista.filter((c) => c.vendidos !== null && c.vendidos !== undefined && c.vendidos_fonte !== 'nao_medido').length, total: lista.length }
}
